import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyVehicleMapDataQuery } from "../../../api/network/adminApiServices";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { calculatedTimeFilters } from "../util/timeFilter";
import { getDayColor } from "./dayColors";
import { formatDateLabel } from "./dateUtils";

export const MAX_SELECTED_DATES = 7;

export type DateGroup = {
  dateKey: string;
  dayIndex: number;
  label: string;
  color: string;
  isFetching: boolean;
  isError: boolean;
  trips: VehicleMapDataPoint[][];
};

// vehicle-map-data accepts one vehicle and one time range per request. Keep
// the same bounded fan-out strategy as the multi-vehicle map rather than
// sending one large range request or an unbounded burst of date requests.
const MAX_CONCURRENT_REQUESTS = 6;
const LOAD_DEBOUNCE_MS = 350;

export const useMultiDateTripData = ({
  accountId,
  deviceId,
  selectedDates,
  timezone,
}: {
  accountId?: string;
  deviceId?: string;
  selectedDates: string[];
  timezone: string;
}) => {
  const [fetchVehicleMapData] = useLazyVehicleMapDataQuery();
  const [dataByDate, setDataByDate] = useState<
    Record<string, VehicleMapDataPoint[][]>
  >({});
  const [errorsByDate, setErrorsByDate] = useState<Record<string, boolean>>(
    {}
  );
  const [isFetching, setIsFetching] = useState(false);
  const runIdRef = useRef(0);

  const load = useCallback(async () => {
    const runId = ++runIdRef.current;
    const isStale = () => runId !== runIdRef.current;

    if (!accountId || !deviceId || selectedDates.length === 0) {
      setDataByDate((previous) =>
        Object.keys(previous).length === 0 ? previous : {}
      );
      setErrorsByDate((previous) =>
        Object.keys(previous).length === 0 ? previous : {}
      );
      setIsFetching(false);
      return;
    }

    setDataByDate({});
    setErrorsByDate({});
    setIsFetching(true);

    const results: Record<string, VehicleMapDataPoint[][]> = {};
    const errors: Record<string, boolean> = {};
    let nextIndex = 0;

    const runWorker = async () => {
      while (!isStale()) {
        const dateKey = selectedDates[nextIndex++];
        if (!dateKey) return;

        const range = calculatedTimeFilters(dateKey, timezone);
        if (!range?.calculatedUnixTimestampFrom) {
          errors[dateKey] = true;
          continue;
        }

        try {
          const data = await fetchVehicleMapData({
            account_id: `${accountId}`,
            device_id: `${deviceId}`,
            start_timestamp: range.calculatedUnixTimestampFrom,
            end_timestamp: range.calculatedUnixTimestampTo ?? 0,
            sort_time: "asc",
          }).unwrap();
          results[dateKey] = Object.values(data || {}).filter(
            (tripPoints) => tripPoints.length > 0
          );
        } catch {
          if (!isStale()) errors[dateKey] = true;
        }
      }
    };

    await Promise.all(
      Array.from(
        { length: Math.min(MAX_CONCURRENT_REQUESTS, selectedDates.length) },
        runWorker
      )
    );

    if (isStale()) return;
    setDataByDate(results);
    setErrorsByDate(errors);
    setIsFetching(false);
  }, [accountId, deviceId, fetchVehicleMapData, selectedDates, timezone]);

  // one date is real work too - the asset map runs the multi-day pipeline
  // at every selection size, so a single day renders the same way a week does
  const hasWork = !!accountId && !!deviceId && selectedDates.length > 0;

  useEffect(() => {
    if (!hasWork) {
      load();
      return;
    }
    setIsFetching(true);
    const timer = setTimeout(load, LOAD_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [hasWork, load]);

  const dateGroups: DateGroup[] = useMemo(() => {
    return selectedDates.map((dateKey, index) => ({
      dateKey,
      dayIndex: index,
      label: formatDateLabel(dateKey),
      color: getDayColor(index),
      isFetching,
      isError: !!errorsByDate[dateKey],
      trips: dataByDate[dateKey] || [],
    }));
  }, [dataByDate, errorsByDate, isFetching, selectedDates]);

  return { dateGroups, isFetching };
};
